import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, isDevMode } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ICustomProperties } from '@microsoft/applicationinsights-core-js';
import { ApplicationInsights, DistributedTracingModes, SeverityLevel } from '@microsoft/applicationinsights-web';
import { buildVariables } from '@th-common/assets/config/build-variables';
import { filter } from 'rxjs/operators';

import { APP_CONFIG } from '@core/constants/app-config.constants';
import { IAppConfig } from '@core/interfaces/app-config.interface';

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private _appInsights: ApplicationInsights;

  constructor(private readonly router: Router, @Inject(APP_CONFIG) private appConfig: IAppConfig) {
    if (!isDevMode()) {
      this._appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: appConfig.appInsightsKey,
          // send W3C traceparent on API calls so browser AJAX dependencies and
          // server requests share an operation id; restricted to the API host so
          // third-party calls (blob storage SAS uploads etc.) stay untouched.
          // The API CORS policy allows any header, so preflights pass.
          enableCorsCorrelation: true,
          distributedTracingMode: DistributedTracingModes.W3C,
          correlationHeaderDomains: this._getCorrelationDomains(appConfig),
          enableUnhandledPromiseRejectionTracking: true,
          enableAjaxErrorStatusText: true,
        },
      });
      this._appInsights.loadAppInsights();
      this._loadCustomTelemetryProperties();
      this._createRouterSubscription();
    }
  }

  setAIUserId(userId: string, accountId: string = '', storeInCookie: boolean = true): void {
    if (!isDevMode() && !!userId) {
      this._appInsights.setAuthenticatedUserContext(userId, accountId, storeInCookie);
    }
  }

  clearAIUserId(): void {
    if (!isDevMode()) {
      this._appInsights.clearAuthenticatedUserContext();
    }
  }

  logPageView(name?: string, uri?: string): void {
    if (!isDevMode()) {
      this._appInsights.trackPageView({
        name,
        uri,
      });
    }
  }

  logException(exception: Error, customProperties: ICustomProperties = {}, severityLevel?: SeverityLevel): void {
    if (!isDevMode()) {
      if (!!exception && !(exception instanceof HttpErrorResponse)) {
        if (exception.stack) {
          exception.name = exception.name || exception.stack.split('\n')[0];
          exception.message = exception.message || exception.stack.split('\n')[0];
        }
        this._appInsights.trackException({ exception }, customProperties);
      }
    }
  }

  logEvent(name: string, properties?: Record<string, any>): void {
    if (!isDevMode()) {
      this._appInsights.trackEvent({ name }, properties);
    }
  }

  logMetric(name: string, average: number, properties?: Record<string, any>): void {
    if (!isDevMode()) {
      this._appInsights.trackMetric({ name, average }, properties);
    }
  }

  logTrace(message: string, properties?: Record<string, any>): void {
    if (!isDevMode()) {
      this._appInsights.trackTrace({ message }, properties);
    }
  }

  private _loadCustomTelemetryProperties(): void {
    this._appInsights.addTelemetryInitializer(envelope => {
      const item = envelope.baseData;
      item.properties = item.properties || {};
      if (!navigator.onLine) {
        // stamp instead of dropping — offline/flaky-network sessions are exactly
        // the ones investigations need evidence from; the SDK buffers and retries
        item.properties.OfflineAtCapture = 'true';
      }
      item.properties.PortalType = this.appConfig.portalType;
      item.properties.FEBuildNumber = buildVariables.buildNumber;
    });
  }

  private _getCorrelationDomains(appConfig: IAppConfig): string[] {
    try {
      return [new URL(appConfig.apiBaseUrl, window.location.origin).hostname];
    } catch (e) {
      return [window.location.hostname];
    }
  }

  private _createRouterSubscription(): void {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.logPageView(null, event.urlAfterRedirects);
    });
  }
}
